import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import moment from "moment/moment";
import momentTZ from "moment-timezone";

import { CONSENT_TO_TREATMENT, HIPAA, PRIVACY, TERMS_AND_CONDITIONS } from "@/core/constants";
import { DownloadButton } from "@/ui2/molecules/DownloadButton";
import { DashboardCard2 } from "@/ui2/molecules/DashboardCard2";
import { Text, TextColors, TextTypes } from "../../../../component-library";
import { ListItem, ListTitleBar, TitleBarItem } from "../../../UI/ListView";
import { getAutomationDataAttr } from "../../../../utils/automationUtils";
import { viewPatientPolicyFile } from "../../../../utils/fileUtil";

import "./style.scss";

/** We need to filter out ADVANCED_CARE_PROGRAM policy
 * It will be displayed in a different section
 */
const SUPPORTED_POLICIES = [CONSENT_TO_TREATMENT, HIPAA, TERMS_AND_CONDITIONS, PRIVACY];

const MemberProfilePolicyList = (props) => {
  const { consents, patientId } = props;
  const [consentData, setConsentData] = useState([]);

  const getPatientConsent = async () => {
    const sortedConsentData = consents
      .filter((consent) => SUPPORTED_POLICIES.includes(consent?.consent_form_name))
      .filter((consent) => consent?.consent_form_version != null)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    setConsentData(sortedConsentData);
  };

  const getPolicyFriendlyName = (consentFormName) => {
    const policyFriendlyNames = {
      CONSENT_TO_TREATMENT: "Consent to Treatment",
      HIPAA: "HIPAA Notice of Privacy Practices",
      TERMS_AND_CONDITIONS: "Terms and Conditions of Use",
      PRIVACY: "Privacy Policy",
    };
    return policyFriendlyNames[consentFormName];
  };

  const buildListItemData = (consent) => {
    return {
      consentGivenBy: consent["response_given_by_name"],
      dateAgreed: moment.utc(consent.timestamp).tz(momentTZ.tz.guess()).format("M/D/YYYY hh:mm a"),
      effectiveDate: consent["consent_form_effective_date"],
      fileKey: consent["user_file_report_key"],
      fileSubType: consent["consent_form_name"],
      policyName: getPolicyFriendlyName(consent["consent_form_name"]),
    };
  };

  const downloadIcon = (listItemData) => {
    let testId = "patient-policy-list-item-download";

    try {
      testId += `-${listItemData.fileSubType.toLowerCase().replaceAll("_", "-")}`;
    } catch (e) {
      console.log(e);
    }

    return (
      <DownloadButton
        onPress={() =>
          viewPatientPolicyFile(patientId, listItemData.fileKey, listItemData.fileSubType)
        }
        {...getAutomationDataAttr(testId)}
      />
    );
  };

  const desktopListItem = (consent) => {
    const listItemData = buildListItemData(consent);
    return (
      <div
        className="row no-gutters d-none d-lg-flex fs-14 align-items-center justify-content-between"
        {...getAutomationDataAttr("patient-policy-list-item")}
      >
        <span className="col-5">{listItemData.policyName}</span>
        <span className="col-2">{listItemData.effectiveDate}</span>
        <span className="col-2">{listItemData.consentGivenBy}</span>
        <span className="col-2">{listItemData.dateAgreed}</span>
        <span className="col-1">{listItemData.fileKey && downloadIcon(listItemData)}</span>
      </div>
    );
  };

  const mobileListItem = (consent) => {
    const listItemData = buildListItemData(consent);
    return (
      <div className="d-flex d-lg-none justify-content-between">
        <div className="d-flex fs-14 flex-column" style={{ wordWrap: "break-word" }}>
          <p className={"tw:font-bold"}>{listItemData.policyName}</p>
          <p>Effective Date: {listItemData.effectiveDate}</p>
          <p>Consent Given By: {listItemData.consentGivenBy}</p>
          <p>Date Agreed: {listItemData.dateAgreed}</p>
          {listItemData.fileKey && <p>Download: {downloadIcon(listItemData)}</p>}
        </div>
      </div>
    );
  };

  const renderConsentList = () => {
    return (
      <>
        {consentData?.length > 0 && (
          <div {...getAutomationDataAttr("patient-policy-list-items-exist")}>
            <ListTitleBar className={"patient-policy-list-title-bar tw:!px-0"}>
              <TitleBarItem
                classes="col-5 patient-policy-list-title-bar-col-header"
                text="Policy Name"
              />
              <TitleBarItem
                classes="col-2 patient-policy-list-title-bar-col-header"
                text="Effective Date"
              />
              <TitleBarItem
                classes="col-2 patient-policy-list-title-bar-col-header"
                text="Consent Given By"
              />
              <TitleBarItem
                classes="col-2 patient-policy-list-title-bar-col-header"
                text="Date Agreed"
              />
              <TitleBarItem classes="col-1 patient-policy-list-title-bar-col-header" text="" />
            </ListTitleBar>

            <ul>
              {consentData.map((consent) => {
                return (
                  <ListItem
                    key={consent.sort_key}
                    testId={"patient-policy-list-item"}
                    className="tw:lg:!px-0"
                  >
                    {desktopListItem(consent)}
                    {mobileListItem(consent)}
                  </ListItem>
                );
              })}
            </ul>
          </div>
        )}
        {consentData?.length === 0 && (
          <div
            className={"py-4 px-lg-3 mx-lg-3"}
            {...getAutomationDataAttr("patient-policy-list-no-items-exist")}
          >
            <Text type={TextTypes.light} color={TextColors.grey}>
              No patient policies currently on file
            </Text>
          </div>
        )}
      </>
    );
  };

  useEffect(() => {
    (async () => {
      await getPatientConsent();
    })();
  }, []);

  return (
    <DashboardCard2 {...getAutomationDataAttr("patient-policy-list")} title="Patient Policies">
      {renderConsentList()}
    </DashboardCard2>
  );
};

MemberProfilePolicyList.propTypes = {
  patientDetails: PropTypes.object,
  profile: PropTypes.object,
};

const mapStateToProps = (state) => {
  return {
    profile: state.auth.profile,
  };
};

export default connect(mapStateToProps)(MemberProfilePolicyList);
